import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const API_URL = API;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+/;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fmtDay(iso) {
  if (!iso) return "Date TBA";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function sessionDateLabel(s) {
  if (!s || !s.start_date) return "Date TBA";
  const a = fmtDay(s.start_date);
  if (s.end_date && s.end_date !== s.start_date) return `${a} – ${fmtDay(s.end_date)}`;
  return a;
}

const inputCls =
  "w-full rounded-lg border bg-white/[0.04] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice/70";

function Field({ label, required = true, error, children, testid, hint }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mb-1.5 flex items-center gap-1 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">
        {label} {required && <span className="text-crimson">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-400">This field is required</span>}
      {hint && !error && <span className="mt-1 block text-[11px] leading-snug text-slate-500">{hint}</span>}
    </label>
  );
}

function SubmitBtn({ testid, sending, children, className = "" }) {
  return (
    <button
      type="submit"
      data-testid={testid}
      disabled={sending}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-crimson px-7 py-3.5 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:bg-crimson-bright active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice disabled:opacity-60 ${className}`}
    >
      {sending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" /> Sending
        </>
      ) : (
        children
      )}
    </button>
  );
}

function errCls(errors, key) {
  return `${inputCls} ${errors[key] ? "border-red-400/70" : "border-white/10"}`;
}

/* ------------------------------------------------------------------ */
/* Apply Now form (Home page)                                          */
/* ------------------------------------------------------------------ */

const APPLY_EMPTY = {
  athlete_name: "",
  athlete_phone: "",
  athlete_email: "",
  high_school: "",
  position: "",
  height: "",
  weight: "",
  parent_name: "",
  parent_phone: "",
  parent_email: "",
};

export function ApplyForm() {
  const [form, setForm] = useState(APPLY_EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: false }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    Object.keys(APPLY_EMPTY).forEach((k) => {
      if (!form[k].trim()) errs[k] = true;
    });
    if (form.athlete_email && !EMAIL_RE.test(form.athlete_email)) errs.athlete_email = true;
    if (form.parent_email && !EMAIL_RE.test(form.parent_email)) errs.parent_email = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete every field — parent contact info included.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/apply`, form);
      toast.success("Application received. Our staff will reach out to you and your family soon.");
      setForm(APPLY_EMPTY);
    } catch {
      toast.error("Submission failed — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="apply-now-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Athlete Full Name" testid="apply-athlete-name-field" error={errors.athlete_name}>
          <input
            data-testid="apply-athlete-name-input"
            value={form.athlete_name}
            onChange={set("athlete_name")}
            placeholder="Jordan Malik James"
            className={errCls(errors, "athlete_name")}
          />
        </Field>
        <Field label="Athlete Phone" testid="apply-athlete-phone-field" error={errors.athlete_phone}>
          <input
            data-testid="apply-athlete-phone-input"
            type="tel"
            value={form.athlete_phone}
            onChange={set("athlete_phone")}
            placeholder="(334) 555-0142"
            className={errCls(errors, "athlete_phone")}
          />
        </Field>
        <Field label="Athlete Email" testid="apply-athlete-email-field" error={errors.athlete_email}>
          <input
            data-testid="apply-athlete-email-input"
            type="email"
            value={form.athlete_email}
            onChange={set("athlete_email")}
            placeholder="you@email.com"
            className={errCls(errors, "athlete_email")}
          />
        </Field>
        <Field label="High School" testid="apply-high-school-field" error={errors.high_school}>
          <input
            data-testid="apply-high-school-input"
            value={form.high_school}
            onChange={set("high_school")}
            placeholder="Tuskegee High School"
            className={errCls(errors, "high_school")}
          />
        </Field>
        <Field label="Position" testid="apply-position-field" error={errors.position}>
          <input
            data-testid="apply-position-input"
            value={form.position}
            onChange={set("position")}
            placeholder="QB, RB, SG, Forward…"
            className={errCls(errors, "position")}
          />
        </Field>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Height" testid="apply-height-field" error={errors.height}>
            <input
              data-testid="apply-height-input"
              value={form.height}
              onChange={set("height")}
              placeholder={'6\'1"'}
              className={errCls(errors, "height")}
            />
          </Field>
          <Field label="Weight" testid="apply-weight-field" error={errors.weight}>
            <input
              data-testid="apply-weight-input"
              value={form.weight}
              onChange={set("weight")}
              placeholder="210 lbs"
              className={errCls(errors, "weight")}
            />
          </Field>
        </div>
      </div>

      <div className="rounded-xl border border-ice/20 bg-ice/5 p-5">
        <p className="eyebrow !text-[10px] mb-4">Parent / Guardian Contact</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Full Name" testid="apply-parent-name-field" error={errors.parent_name}>
            <input
              data-testid="apply-parent-name-input"
              value={form.parent_name}
              onChange={set("parent_name")}
              placeholder="Angela James"
              className={errCls(errors, "parent_name")}
            />
          </Field>
          <Field label="Phone" testid="apply-parent-phone-field" error={errors.parent_phone}>
            <input
              data-testid="apply-parent-phone-input"
              type="tel"
              value={form.parent_phone}
              onChange={set("parent_phone")}
              placeholder="(334) 555-0199"
              className={errCls(errors, "parent_phone")}
            />
          </Field>
          <Field label="Email" testid="apply-parent-email-field" error={errors.parent_email}>
            <input
              data-testid="apply-parent-email-input"
              type="email"
              value={form.parent_email}
              onChange={set("parent_email")}
              placeholder="parent@email.com"
              className={errCls(errors, "parent_email")}
            />
          </Field>
        </div>
      </div>

      <SubmitBtn testid="apply-submit-btn" sending={sending} className="w-full sm:w-auto">
        Submit Application
      </SubmitBtn>
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
        Applications go straight to our staff — expect a call for you and your parents.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Recruiting profile form (Recruiting page)                           */
/* ------------------------------------------------------------------ */

const PROSPECT_EMPTY = {
  full_name: "",
  graduation_year: "",
  school: "",
  position: "",
  height: "",
  weight: "",
  film_link: "",
  parent_name: "",
  parent_phone: "",
  parent_email: "",
};

export function RecruitingForm() {
  const [form, setForm] = useState(PROSPECT_EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [film, setFilm] = useState({ url: "", name: "", uploading: false });

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: false }));
  };

  const uploadFilm = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("That file is too large — 100MB max.");
      return;
    }
    setFilm((s) => ({ ...s, uploading: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/film`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFilm({ url: data.url, name: file.name, uploading: false });
      toast.success("Film file uploaded.");
    } catch (err) {
      setFilm((s) => ({ ...s, uploading: false }));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed — please try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    Object.keys(PROSPECT_EMPTY).forEach((k) => {
      if (k !== "film_link" && !form[k].trim()) errs[k] = true;
    });
    if (form.parent_email && !EMAIL_RE.test(form.parent_email)) errs.parent_email = true;
    if (form.film_link && !URL_RE.test(form.film_link)) errs.film_link = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Complete the profile — parents, we need your contact info too.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/recruiting`, {
        ...form,
        film_file_url: film.url,
        film_file_name: film.name,
      });
      toast.success("Profile received, recruit. Our staff will be in touch with your family.");
      setForm(PROSPECT_EMPTY);
      setFilm({ url: "", name: "", uploading: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="recruiting-entry-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" testid="recruiting-full-name-field" error={errors.full_name}>
          <input
            data-testid="recruiting-full-name-input"
            value={form.full_name}
            onChange={set("full_name")}
            placeholder="Devin O'Neal"
            className={errCls(errors, "full_name")}
          />
        </Field>
        <Field label="Graduation Year" testid="recruiting-grad-year-field" error={errors.graduation_year}>
          <select
            data-testid="recruiting-grad-year-input"
            value={form.graduation_year}
            onChange={set("graduation_year")}
            className={`${errCls(errors, "graduation_year")} appearance-none ${form.graduation_year ? "" : "text-slate-500"}`}
          >
            <option value="" disabled className="bg-navy-800">
              Select year
            </option>
            {["2026", "2027", "2028", "2029"].map((y) => (
              <option key={y} value={y} className="bg-navy-800 text-white">
                Class of {y}
              </option>
            ))}
          </select>
        </Field>
        <Field label="School You Attend" testid="recruiting-school-field" error={errors.school}>
          <input
            data-testid="recruiting-school-input"
            value={form.school}
            onChange={set("school")}
            placeholder="Booker T. Washington High"
            className={errCls(errors, "school")}
          />
        </Field>
        <Field label="Position" testid="recruiting-position-field" error={errors.position}>
          <input
            data-testid="recruiting-position-input"
            value={form.position}
            onChange={set("position")}
            placeholder="WR, Guard, Center…"
            className={errCls(errors, "position")}
          />
        </Field>
        <Field label="Height" testid="recruiting-height-field" error={errors.height}>
          <input
            data-testid="recruiting-height-input"
            value={form.height}
            onChange={set("height")}
            placeholder={'6\'4"'}
            className={errCls(errors, "height")}
          />
        </Field>
        <Field label="Weight" testid="recruiting-weight-field" error={errors.weight}>
          <input
            data-testid="recruiting-weight-input"
            value={form.weight}
            onChange={set("weight")}
            placeholder="185 lbs"
            className={errCls(errors, "weight")}
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="High School Film Link"
          required={false}
          testid="recruiting-film-link-field"
          error={errors.film_link}
          hint="Hudl, YouTube or any public link to your film."
        >
          <input
            data-testid="recruiting-film-link-input"
            value={form.film_link}
            onChange={set("film_link")}
            placeholder="https://www.hudl.com/video/…"
            className={form.film_link && !URL_RE.test(form.film_link) ? `${inputCls} border-red-400/70` : `${inputCls} border-white/10`}
          />
        </Field>
        <Field
          label="…or Upload a Highlight File"
          required={false}
          testid="recruiting-film-file-field"
          hint="Video, image or PDF — up to 100MB."
        >
          <input
            type="file"
            accept="video/*,image/*,application/pdf"
            data-testid="recruiting-film-file-input"
            onChange={uploadFilm}
            disabled={film.uploading}
            className="hidden"
            id="recruiting-film-file"
          />
          <label
            htmlFor="recruiting-film-file"
            className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-white/[0.04] px-4 py-3 text-sm text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
          >
            {film.uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" /> {film.name || "Choose a file"}
              </>
            )}
          </label>
        </Field>
      </div>

      <div className="rounded-xl border border-ice/20 bg-ice/5 p-5">
        <p className="eyebrow !text-[10px] mb-4">Parent / Guardian Contact</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Full Name" testid="recruiting-parent-name-field" error={errors.parent_name}>
            <input
              data-testid="recruiting-parent-name-input"
              value={form.parent_name}
              onChange={set("parent_name")}
              placeholder="Marcus O'Neal"
              className={errCls(errors, "parent_name")}
            />
          </Field>
          <Field label="Phone" testid="recruiting-parent-phone-field" error={errors.parent_phone}>
            <input
              data-testid="recruiting-parent-phone-input"
              type="tel"
              value={form.parent_phone}
              onChange={set("parent_phone")}
              placeholder="(334) 555-0117"
              className={errCls(errors, "parent_phone")}
            />
          </Field>
          <Field label="Email" testid="recruiting-parent-email-field" error={errors.parent_email}>
            <input
              data-testid="recruiting-parent-email-input"
              type="email"
              value={form.parent_email}
              onChange={set("parent_email")}
              placeholder="parent@email.com"
              className={errCls(errors, "parent_email")}
            />
          </Field>
        </div>
      </div>

      <SubmitBtn testid="recruiting-submit-btn" sending={sending} className="w-full sm:w-auto">
        Enter My Info
      </SubmitBtn>
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
        Your profile and film go directly to our recruiting staff — no portals, no middlemen.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Contact form (Contact page)                                         */
/* ------------------------------------------------------------------ */

const CONTACT_EMPTY = {
  full_name: "",
  phone: "",
  email: "",
  role_type: "",
  preferred_contact: "Call Back",
  message: "",
};

export function ContactForm() {
  const [form, setForm] = useState(CONTACT_EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: false }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    ["full_name", "phone", "email", "role_type", "message"].forEach((k) => {
      if (!form[k].trim()) errs[k] = true;
    });
    if (form.email && !EMAIL_RE.test(form.email)) errs.email = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Fill in your info and message so our staff can reach you.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Our staff will call or email you back.");
      setForm(CONTACT_EMPTY);
    } catch {
      toast.error("Submission failed — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" testid="contact-name-field" error={errors.full_name}>
          <input
            data-testid="contact-name-input"
            value={form.full_name}
            onChange={set("full_name")}
            placeholder="Your name"
            className={errCls(errors, "full_name")}
          />
        </Field>
        <Field label="Phone" testid="contact-phone-field" error={errors.phone}>
          <input
            data-testid="contact-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="(334) 555-0100"
            className={errCls(errors, "phone")}
          />
        </Field>
        <Field label="Email" testid="contact-email-field" error={errors.email}>
          <input
            data-testid="contact-email-input"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@email.com"
            className={errCls(errors, "email")}
          />
        </Field>
        <Field label="I Am A…" testid="contact-role-field" error={errors.role_type}>
          <select
            data-testid="contact-role-select"
            value={form.role_type}
            onChange={set("role_type")}
            className={`${errCls(errors, "role_type")} appearance-none ${form.role_type ? "" : "text-slate-500"}`}
          >
            <option value="" disabled className="bg-navy-800">
              Choose one
            </option>
            {["Athlete", "Parent / Family", "Coach", "Other"].map((r) => (
              <option key={r} value={r} className="bg-navy-800 text-white">
                {r}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div>
        <span className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">
          How should we reach you back?
        </span>
        <div className="flex flex-wrap gap-3">
          {["Call Back", "Email Back"].map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`contact-pref-${opt.split(" ")[0].toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, preferred_contact: opt }))}
              className={`rounded-full border px-5 py-2 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
                form.preferred_contact === opt
                  ? "border-ice bg-ice/10 text-ice"
                  : "border-white/15 text-silver-dim hover:border-ice/50 hover:text-white"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <Field label="Your Message" testid="contact-message-field" error={errors.message}>
        <textarea
          data-testid="contact-message-input"
          value={form.message}
          onChange={set("message")}
          rows={5}
          placeholder="Tell our staff what you need — questions about the program, eligibility, camps, anything."
          className={`${errCls(errors, "message")} resize-none`}
        />
      </Field>

      <SubmitBtn testid="contact-submit-btn" sending={sending} className="w-full sm:w-auto">
        Send Message
      </SubmitBtn>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Notify form (Schedule / Roster release alerts)                      */
/* ------------------------------------------------------------------ */

export function NotifyForm({ interest, testidPrefix, cta = "Notify Me" }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/notify`, { email, interest });
      toast.success("You're on the list — we'll alert you the moment it drops.");
      setEmail("");
    } catch {
      toast.error("Submission failed — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row" data-testid={`${testidPrefix}-form`}>
      <input
        data-testid={`${testidPrefix}-email-input`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        aria-label="Email address"
        className={`${inputCls} border-white/10 sm:flex-1`}
      />
      <SubmitBtn testid={`${testidPrefix}-submit`} sending={sending}>
        {cta}
      </SubmitBtn>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Camp registration form (Camps page)                                 */
/* ------------------------------------------------------------------ */

const CAMP_EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  camp: "",
  graduation_year: "",
  session_id: "general",
};

export function CampForm({ sessions = [], selectedSessionId = "" }) {
  const [form, setForm] = useState(CAMP_EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (selectedSessionId) {
      setForm((f) => ({ ...f, session_id: selectedSessionId }));
    }
  }, [selectedSessionId]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: false }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    ["full_name", "email", "phone", "camp"].forEach((k) => {
      if (!form[k].trim()) errs[k] = true;
    });
    if (form.email && !EMAIL_RE.test(form.email)) errs.email = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Add your name, contact info and the camp you're interested in.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/camps/register`, {
        ...form,
        session_id: form.session_id === "general" ? "" : form.session_id,
      });
      toast.success("Camp registration received — we'll contact you with confirmation and details.");
      setForm(CAMP_EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="camp-register-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" testid="camp-name-field" error={errors.full_name}>
          <input
            data-testid="camp-name-input"
            value={form.full_name}
            onChange={set("full_name")}
            placeholder="Athlete name"
            className={errCls(errors, "full_name")}
          />
        </Field>
        <Field label="Email" testid="camp-email-field" error={errors.email}>
          <input
            data-testid="camp-email-input"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@email.com"
            className={errCls(errors, "email")}
          />
        </Field>
        <Field label="Phone" testid="camp-phone-field" error={errors.phone}>
          <input
            data-testid="camp-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="(334) 555-0173"
            className={errCls(errors, "phone")}
          />
        </Field>
        <Field label="Camp" testid="camp-select-field" error={errors.camp}>
          <select
            data-testid="camp-select"
            value={form.camp}
            onChange={set("camp")}
            className={`${errCls(errors, "camp")} appearance-none ${form.camp ? "" : "text-slate-500"}`}
          >
            <option value="" disabled className="bg-navy-800">
              Choose a camp
            </option>
            {[
              "Post-Grad Showcase Camp",
              "High School Camp (Juniors & Seniors)",
              "Tuskegee Community Camp",
            ].map((c) => (
              <option key={c} value={c} className="bg-navy-800 text-white">
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Camp Session" required={false} testid="camp-session-select-field">
          <select
            data-testid="camp-session-select"
            value={form.session_id}
            onChange={set("session_id")}
            className={`${inputCls} border-white/10 appearance-none`}
          >
            <option value="general" className="bg-navy-800 text-white">
              General interest — no specific date
            </option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id} className="bg-navy-800 text-white">
                {s.title} — {sessionDateLabel(s)}
                {s.capacity > 0 ? ` (${s.spots_left} spots left)` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Graduation Year" required={false} testid="camp-grad-year-field">
          <input
            data-testid="camp-grad-year-input"
            value={form.graduation_year}
            onChange={set("graduation_year")}
            placeholder="e.g. 2027 (optional)"
            className={`${inputCls} border-white/10`}
          />
        </Field>
      </div>

      <SubmitBtn testid="camp-register-submit" sending={sending} className="w-full sm:w-auto">
        {form.session_id !== "general" ? "Reserve My Spot" : "Register My Interest"}
      </SubmitBtn>
    </form>
  );
}
